import { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { colors } from '../styles/colors';
import Atol from '../assets/images/ATOL-9759-white.png';
import Image from './Image';
import StarIcon from '@mui/icons-material/Star';
import Feefo from '../assets/images/Platinum-Trusted-Service-Award-2025.png';
import useWindowWidth from '../hooks/useWindowWidth';
import Bbta from '../assets/images/British-Travel-Awards-summary.png';
import AbtaIE from '../assets/images/ie-protected-2023.png';
import Screen1 from '../assets/images/Loading-1-size.jpg';
import Screen2 from '../assets/images/Loading-2-size.jpg';
import Screen3 from '../assets/images/Loading-3-size.jpg';
import Screen4 from '../assets/images/Loading-4-size.jpg';


interface customContainerProps {
    textChange?: boolean;
}

type Slide = {
    heading: string;
    body: string;
    image: string;
    badge?: 'award' | 'feefo' | 'atol';
    stars?: number;
};

const slides: Slide[] = [
    {
        heading: 'We are highly rated',
        body: 'Our reviews highlight our exceptional service.',
        image: Screen4,
        badge: 'feefo',
    },
    {
        heading: 'We are award-winning',
        body: 'Our awards reflect our expertise in tailor-made holidays',
        image: Screen3,
        badge: 'award',
    },
    {
        heading: '"Beyond our expectations"',
        body: '"The team at Tropical Sky helped us to organise the holiday of a lifetime"',
        image: Screen2,
        stars: 5,
    },
    ...(process.env.REACT_APP_PROJECT_COUNTRY === "ie" ? [] : [{
        heading: 'Your money is safe with us',
        body: "We're ATOL (9759) protected, ensuring your holiday is secure",
        image: Screen1,
        badge: 'atol',
    }] as Slide[]),
];

const LottieLoader = ({ textChange }: customContainerProps) => {
    const windowWidth = useWindowWidth();
    const [activeIndex, setActiveIndex] = useState(0);

    const isView = useMemo(
        () => typeof window !== 'undefined' && window.location.search.includes('isView'),
        []
    );

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % slides.length);
        }, 3000);

        return () => clearInterval(timer);
    }, []);

    const primaryMessage = textChange
        ? isView
            ? 'Your summary is loading. Please wait for a while.'
            : 'Your payment has been successful. We are confirming your booking.'
        : 'Please wait\nwhile we search for your holiday...';

    const secondaryMessage = textChange
        ? isView
            ? ''
            : 'Please do not close your tab / window until the confirmation page loads'
        : 'We are checking for current availability and pricing.';
    const PANEL_WIDTH = '49%';

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                },
                '& .MuiDialog-paper': {
                    maxWidth: "1250px",
                    width: 'calc(100% - 39px)',
                    margin: '0 auto',
                },
                '& .MuiModal-backdrop': {
                    backgroundColor: colors.basics.secondary,
                },
                '& .MuiPaper-root': {
                    boxShadow: 'none',
                    // backgroundColor: 'transparent',
                    borderRadius: '0',
                    overflow: 'visible',
                    // backgroundColor: 'pink',
                    border: "none",
                    p: "0"
                },

            }}
            open
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    // px: { xs: 2, sm: 3, md: 4 },
                    // py: { xs: 2, sm: 4 },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '1250px',
                        aspectRatio: { xl: '18/9', lg: '16/9', md: '16/9', sm: '14/9', xs: 'unset' },
                        height: { xs: '58vh', sm: 'auto' },
                        maxHeight: { xs: 'calc(100vh - 120px)', sm: 'none' },
                        // borderRadius: '8px',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                    }}
                >
                    {/* Render all slide images as layers with opacity transitions */}
                    {slides.map((slide, index) => (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: activeIndex === index ? 1 : 0,
                                transition: 'opacity 0.7s ease-in-out',
                                pointerEvents: 'none',
                                backgroundImage: {
                                    xs: `
                                      linear-gradient(
                                        
                                        rgba(52, 45, 45, 0.75),
                                        rgba(52, 45, 45, 0.75)
                                      ),
                                      url(${slide.image})
                                    `,
                                    md: `
                                      linear-gradient(
                                        90deg,
                                        rgba(52, 45, 45, 0.75) 0%,
                                        rgba(52, 45, 45, 0.75) ${PANEL_WIDTH},
                                        rgba(52, 45, 45, 0) ${PANEL_WIDTH}
                                      ),
                                      url(${slide.image})
                                    `,
                                },
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center center',
                            }}
                        />
                    ))}
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            color: colors.basics.white,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: { xs: 1.5, sm: '40px', md: '60px' },
                            width: { xs: '100%', md: PANEL_WIDTH },
                            px: { xs: 3, sm: 5, md: 6 },
                            py: { xs: 3, sm: 5 },
                            minHeight: { xs: 'auto', sm: '400px' },
                        }}
                    >
                        <Box
                            sx={{
                                mb: { xs: 2, sm: 2.5, md: 3 },
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontSize={{ xs: 18, sm: 22, md: 30 }}
                                fontWeight={700}
                                lineHeight={1.2}
                                color={colors.basics.white}
                                sx={{ whiteSpace: 'pre-line' }}
                            >
                                {primaryMessage}
                            </Typography>
                        </Box>
                        {/* {secondaryMessage && (
                            <Typography
                                mt={0.7}
                                fontSize={{ xs: 13, sm: 14, md: 15 }}
                                lineHeight={1.3}
                                sx={{ opacity: 0.9 }}
                                color={colors.basics.white}
                            >
                                {secondaryMessage}
                            </Typography>
                        )} */}
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                minHeight: { xs: '200px', sm: '250px', md: '300px' },
                            }}
                        >
                            {slides.map((slide, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: { xs: 1.2, sm: 1.6 },
                                        opacity: activeIndex === index ? 1 : 0,
                                        transition: 'opacity 0.7s ease-in-out',
                                        pointerEvents: activeIndex === index ? 'auto' : 'none',
                                    }}
                                >
                                    <Typography
                                        mt={{ xs: 3, sm: 3.5 }}
                                        variant="h4"
                                        fontSize={{ xs: 18, sm: 24, md: 30 }}
                                        fontWeight={700}
                                        lineHeight={1.2}
                                        color={colors.basics.white}
                                    >
                                        {slide.heading}
                                    </Typography>

                                    <Typography
                                        mt={1.2}
                                        fontSize={{ xs: 14, sm: 16, md: 20 }}
                                        lineHeight={1.35}
                                        sx={{ maxWidth: { xs: '100%', md: 260 } }}
                                        color={colors.basics.white}
                                        variant='body1'
                                        fontWeight={500}
                                    >
                                        {slide.body}
                                    </Typography>
                                    {slide.badge === 'award' && (
                                        <Image
                                            src={process.env.REACT_APP_PROJECT_COUNTRY === "ie" ? AbtaIE : Bbta}
                                            style={{
                                                maxWidth: windowWidth > 900 ? '220px' : '180px',
                                                // marginTop: '14px',
                                            }}
                                        />
                                    )}
                                    {slide.badge === 'feefo' && (
                                        <Image
                                            src={Feefo}
                                            style={{
                                                maxWidth: windowWidth > 900 ? '200px' : '140px',
                                                marginTop: '14px',
                                            }}
                                        />
                                    )}
                                    {slide.badge === 'atol' && (
                                        <Image
                                            src={Atol}
                                            style={{
                                                maxWidth: windowWidth > 900 ? '100px' : '90px',
                                                marginTop: '14px',
                                            }}
                                        />
                                    )}
                                    {slide.stars && (
                                        <Box display="flex" mt={2} gap={0.7}>
                                            {Array.from({ length: slide.stars }).map((_, idx) => (
                                                <StarIcon key={idx} sx={{ color: colors.basics.white, fontSize: 30 }} />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '1250px',
                        mt: { xs: 3, sm: 4 },
                        // px: { xs: 1, sm: 2 },
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: 14,
                            backgroundColor: '#d5d5d5',
                            overflow: 'hidden',
                            position: 'relative',
                            // borderRadius: '999px',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '40%',
                                background: 'linear-gradient(90deg, rgba(239,83,35,0) 0%, rgba(239,83,35,1) 50%, rgba(239,83,35,0) 100%)',
                                animation: 'loaderBar 2s linear infinite',
                                '@keyframes loaderBar': {
                                    '0%': {
                                        transform: 'translateX(-100%)',
                                    },
                                    '100%': {
                                        transform: 'translateX(300%)',
                                    },
                                },
                            }}
                        />

                    </Box>
                </Box>

            </Box>
        </Dialog >
    );
};

export default LottieLoader;